import { StatusFilter } from "@/components/status-filter"
import { useBlockNotification } from "@/hooks/useBlockNotification"
import { useSingleFilterParam } from "@/hooks/useSingleFilterParam"
import { DownOutlined } from "@ant-design/icons"
import { Dropdown, Menu, Space, Typography } from "antd"
import { FC, useMemo } from "react"
import { useAuth } from "../../../../../../../components/auth"
import { useBlockUsersMutation, useGetRegionsQuery, useUnblockUsersMutation } from "../../../../../../../graphql"
import { truncate } from "../../../../../../../utils"
import "./index.less"

const Filter: FC<RoleFilterInterface> = ({ filters, specificFilters, setFilters, refresh }) => {
  const { region } = useAuth()
  const { data: regionsData, loading } = useGetRegionsQuery()

  const { value: selectedUsers, resetValue: resetUsers } = useSingleFilterParam("users")
  const [blockUsers] = useBlockUsersMutation()
  const [unblockUsers] = useUnblockUsersMutation()

  const getRegionOptions = () => {
    const regionOptions = Array.from({ length: regionsData?.regions?.data.length || 0 }, (_, i) => ({
      label: regionsData?.regions?.data[i].attributes?.name,
      key: i + 1,
    }))
    regionOptions.unshift({
      label: "All",
      key: 0,
    })
    return regionOptions || []
  }

  let regionOptions = getRegionOptions()
  if (region?.name) {
    const adminRegion = region!.name!
    regionOptions = [{ label: adminRegion, key: 0 }]
  }

  const insiderStatusOptions = ["All", "Allowed", "Not Allowed"].map((label, index) => ({ label, key: index + 1 }))

  const resetFilter = (type: string) => {
    const handleResetFilters = (fieldType: string) => setFilters({ ...filters, [fieldType]: "All" })
    switch (type) {
      case "name":
        handleResetFilters("selectedName")
        break
      case "email":
        handleResetFilters("selectedEmail")
        break
      case "sales-manager":
        handleResetFilters("selectedSM")
        break
      case "distributor":
        handleResetFilters("selectedDistributor")
        break
    }
  }

  const activeSpecificFiltersAmount = specificFilters.length

  const config = useMemo(
    () => ({
      key: "user_ids",
      block: blockUsers,
      unblock: unblockUsers,
      reset: resetUsers,
    }),
    [blockUsers, unblockUsers, resetUsers]
  )

  useBlockNotification({
    selected: selectedUsers || [],
    config,
    onSuccess: refresh,
  })

  return (
    <div className={"filters-wrapper salons-filter"}>
      <div className={"filters"}>
        <Dropdown
          className={`region-filter ${filters.selectedRegion != "All" && "active"}`}
          trigger={["click"]}
          disabled={loading}
          overlay={
            <Menu
              onClick={e =>
                setFilters({ ...filters, selectedRegion: regionOptions.find(item => item.key == +e.key)!.label! })
              }
              selectedKeys={[`${regionOptions.find(item => item.label == filters.selectedRegion)?.key}`]}
            >
              {regionOptions.map(item => (
                <Menu.Item key={item.key}>{item.label}</Menu.Item>
              ))}
            </Menu>
          }
        >
          <Typography.Link>
            <Space className={"dropdown"}>
              {filters.selectedRegion
                ? truncate(regionOptions.find(item => item.label == filters.selectedRegion)?.label ?? "All", 13)
                : "All"}
              <DownOutlined style={{ color: "black" }} />
            </Space>
          </Typography.Link>
        </Dropdown>

        <StatusFilter filters={filters} setFilters={setFilters} />

        <Dropdown
          className={`insider-status-filter ${filters.selectedInsiderStatus != "All" && "active"}`}
          trigger={["click"]}
          overlay={
            <Menu
              onClick={e =>
                setFilters({
                  ...filters,
                  selectedInsiderStatus: insiderStatusOptions.find(item => item.key == +e.key)!.label! || "All",
                })
              }
              selectedKeys={[
                `${insiderStatusOptions.find(item => item.label == filters.selectedInsiderStatus)?.key ?? 0}`,
              ]}
            >
              {insiderStatusOptions.map(item => (
                <Menu.Item key={item.key}>{item.label}</Menu.Item>
              ))}
            </Menu>
          }
        >
          <Typography.Link>
            <Space className={"dropdown"}>
              {truncate(
                insiderStatusOptions.find(item => item.label == filters.selectedInsiderStatus)?.label ?? "All",
                15
              )}
              <DownOutlined style={{ color: "black" }} />
            </Space>
          </Typography.Link>
        </Dropdown>
      </div>

      <div className={"specific-filters-wrapper"}>
        Filters ({activeSpecificFiltersAmount})
        <div className={"specific-filters-list"}>
          {specificFilters &&
            specificFilters.map((filter: any, i: number) => (
              <div
                className={"block"}
                key={`filter-${i}`}
                style={{ backgroundColor: filter.backgroundColor, color: filter.color }}
              >
                {truncate(filter.option, 17)}
                <div className={"remove-filter"} onClick={() => resetFilter(filter.type)}>
                  <div className={"l-line"} />
                  <div className={"r-line"} />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default Filter
